import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "../AuthProvider";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";

const INTERVAL_MS = 10 * 60 * 1000;
const STORAGE_KEY = "blockkingz-closure-notice-dismissed-at";

const BLOCKHQ_URL = "https://app.blockhq.com";

export function ClosureNoticeDialog() {
  const { auth, isLoading } = useAuth();
  const [open, setOpen] = useState(false);
  const timerRef = useRef<number | null>(null);

  const clearTimer = useCallback(() => {
    if (timerRef.current != null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const scheduleNextShow = useCallback(() => {
    clearTimer();
    timerRef.current = window.setTimeout(() => {
      timerRef.current = null;
      setOpen(true);
    }, INTERVAL_MS);
  }, [clearTimer]);

  useEffect(() => {
    if (isLoading || !auth) {
      clearTimer();
      setOpen(false);
      return;
    }

    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) {
      setOpen(true);
      return () => clearTimer();
    }

    const dismissedAt = parseInt(raw, 10);
    if (Number.isNaN(dismissedAt)) {
      setOpen(true);
      return () => clearTimer();
    }

    const elapsed = Date.now() - dismissedAt;
    if (elapsed >= INTERVAL_MS) {
      setOpen(true);
    } else {
      clearTimer();
      timerRef.current = window.setTimeout(() => {
        timerRef.current = null;
        setOpen(true);
      }, INTERVAL_MS - elapsed);
    }

    return () => clearTimer();
  }, [isLoading, auth, clearTimer]);

  const handleOpenChange = (next: boolean) => {
    if (next) {
      setOpen(true);
      return;
    }
    sessionStorage.setItem(STORAGE_KEY, String(Date.now()));
    setOpen(false);
    scheduleNextShow();
  };

  const dismiss = () => handleOpenChange(false);

  if (isLoading || !auth) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Important notice</DialogTitle>
          <DialogDescription>
            Please note that as of April 10th, Blockkingz will be closing its
            operations and transactions will no longer be supported. All future
            transactions should be moved to{" "}
            <a
              href={BLOCKHQ_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium text-primary underline-offset-4 hover:underline"
            >
              {BLOCKHQ_URL}
            </a>
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button type="button" onClick={dismiss}>
            Dismiss
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
